// SPDX-License-Identifier: MIT
// Bridges the real desktop store row types <-> the pure GenesisState. buildGenesisState
// composes a state from a project row + its versions + local sessions (feed stripped);
// the genesisTo* helpers map a parsed state back onto store-shaped values for hydration.
import type { ProjectRow, ProjectVersionRow } from '../projects/api';
import type { Session } from './session-policy';
import { stripSessionForCloud, cloudSessionToLocal, type CloudSession } from './session-sync';
import {
  GENESIS_SCHEMA_VERSION,
  type GenesisState, type GenesisProjectFile, type GenesisVersionFile,
} from './genesis-state';

type ProjectSlice = Pick<ProjectRow,
  'id' | 'title' | 'idea' | 'outline' | 'first_steps' | 'stack' | 'locale'
  | 'discovery_summary' | 'discovery_questions' | 'discovery_answers'
  | 'current_version_number' | 'backend_kind' | 'backend_url' | 'backend_anon_key'>;

export function buildGenesisState(
  project: ProjectSlice,
  versions: ProjectVersionRow[],
  sessions: Session[],
): GenesisState {
  const backendComplete = Boolean(project.backend_kind && project.backend_url && project.backend_anon_key);
  return {
    project: {
      title: project.title,
      idea: project.idea,
      outline: project.outline,
      first_steps: project.first_steps,
      stack: project.stack,
      locale: project.locale,
      discovery: {
        summary: project.discovery_summary,
        questions: project.discovery_questions,
        answers: project.discovery_answers,
      },
      current_version_number: project.current_version_number,
    },
    versions: versions.map((v) => ({
      version_number: v.version_number,
      title: v.title,
      outline: v.outline,
      first_steps: v.first_steps,
      stack: v.stack,
      context_kind: v.context_kind,
      context_snapshot: v.context_snapshot,
      created_at: v.created_at,
    })),
    sessions: sessions.map(stripSessionForCloud),
    backend: backendComplete
      ? { kind: project.backend_kind!, url: project.backend_url!, anon_key: project.backend_anon_key! }
      : null,
    meta: {
      schemaVersion: GENESIS_SCHEMA_VERSION,
      cloudProjectId: project.id,
    },
  };
}

export function genesisToProjectFields(p: GenesisProjectFile): Partial<ProjectRow> {
  return {
    title: p.title,
    idea: p.idea,
    outline: p.outline,
    first_steps: p.first_steps,
    stack: p.stack,
    locale: p.locale as ProjectRow['locale'],
    discovery_summary: p.discovery.summary,
    discovery_questions: p.discovery.questions,
    discovery_answers: p.discovery.answers,
    current_version_number: p.current_version_number,
  };
}

export function genesisToVersionRows(versions: GenesisVersionFile[], localProjectId: string): ProjectVersionRow[] {
  return versions.map((v) => ({
    id: `${localProjectId}:v${v.version_number}`,
    project_id: localProjectId,
    version_number: v.version_number,
    title: v.title,
    outline: v.outline,
    first_steps: v.first_steps,
    stack: v.stack,
    context_kind: v.context_kind,
    context_snapshot: v.context_snapshot,
    created_at: v.created_at,
  }));
}

export function genesisToSessions(sessions: CloudSession[]): Session[] {
  return sessions.map(cloudSessionToLocal);
}
